import React from "react";
import { Link } from "react-router-dom";
import { DatePicker } from "antd";
import dayjs from "dayjs";
import CommonSelect from "../../../core/common/commonSelect";
import { useFeeType } from "../hooks/useFeeType";
import { showToast } from "../../../core/utils/alert";

interface FeeConfigModalProps {
  type?: "config" | "type";
  id?: string | null;
}

const FeeConfigModal: React.FC<FeeConfigModalProps> = ({ type = "config", id }) => {
  const { deleteFeeType } = useFeeType();

  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  const formattedDate = `${month}-${day}-${year}`;
  const defaultValue = dayjs(formattedDate);

  const getModalContainer = () => {
    const modalElement = document.getElementById("modal-datepicker");
    return modalElement ? modalElement : document.body;
  };

  const courseOptions = [
    { value: "mental-arithmetic", label: "Mental Arithmetic" },
    { value: "tiny-dots", label: "Tiny Dots" },
  ];

  const levelOptions = Array.from({ length: 10 }, (_, i) => ({
    value: `level-${i + 1}`,
    label: `Level ${i + 1}`,
  }));

  const sessionOptions = [
    { value: "Jan 2025", label: "Jan 2025" },
    { value: "Feb 2025", label: "Feb 2025" },
    { value: "Mar 2025", label: "Mar 2025" },
  ];

  const handleDelete = async () => {
    if (id) {
      const result = await deleteFeeType(id);
      if (result.success) {
        showToast({
          title: "Success",
          text: result.data?.message || "Record deleted successfully",
          icon: "success",
        });
      } else {
        showToast({
          title: "Error",
          text: result.error || "Could not delete the record",
          icon: "error",
        });
        console.error(result.error);
      }
    }
  };

  return (
    <>
      <div className="modal fade" id="addFeeConfig">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <div className="d-flex align-items-center">
                <h4 className="modal-title">Add Fee Configuration</h4>
              </div>
              <button
                type="button"
                className="btn-close custom-btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form>
              <div id="modal-datepicker" className="modal-body">
                <div className="row">
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Course</label>
                      <CommonSelect
                        className="select"
                        options={courseOptions}
                        defaultValue={courseOptions[0]}
                      />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Level</label>
                      <CommonSelect
                        className="select"
                        options={levelOptions}
                        defaultValue={levelOptions[0]}
                      />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Fee Amount</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter Fee Amount"
                      />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Session</label>
                      <CommonSelect
                        className="select"
                        options={sessionOptions}
                        defaultValue={sessionOptions[0]}
                      />
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="mb-3">
                      <label className="form-label">Effective Date</label>
                      <div className="date-pic">
                        <DatePicker
                          className="form-control datetimepicker"
                          format={{
                            format: "DD-MM-YYYY",
                            type: "mask",
                          }}
                          getPopupContainer={getModalContainer}
                          defaultValue={defaultValue}
                          placeholder="DD-MM-YYYY"
                        />
                        <span className="cal-icon">
                          <i className="ti ti-calendar" />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-6">
                    <div className="modal-satus-toggle d-flex align-items-center justify-content-between mb-3">
                      <div className="status-title">
                        <h5>Status</h5>
                        <p className="mt-3">Change the Status by toggle</p>
                      </div>
                      <div className="status-toggle modal-status">
                        <input
                          type="checkbox"
                          id="fee_status"
                          className="check mt-3"
                          defaultChecked
                        />
                        <label htmlFor="fee_status" className="checktoggle">
                          {" "}
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-lg-12">
                    <div className="mb-0">
                      <label className="form-label">Notes</label>
                      <textarea
                        rows={4}
                        className="form-control"
                        placeholder="Add Notes"
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <Link
                  to="#"
                  className="btn btn-light me-2"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </Link>
                <Link
                  to="#"
                  className="btn btn-primary"
                  data-bs-dismiss="modal"
                >
                  Save Configuration
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Delete Fee Configuration Modal */}
      <div className="modal fade" id="delete-modal">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <form>
              <div className="modal-body text-center">
                <span className="delete-icon">
                  <i className="ti ti-trash" />
                </span>
                <h4>Confirm Deletion</h4>
                <p>
                  You want to delete this{" "}
                  {type === "config" ? "fee configuration" : "fee type"}, this
                  can't be undone once you delete.
                </p>
                <div className="d-flex justify-content-center">
                  <Link
                    to="#"
                    className="btn btn-light me-3"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </Link>
                  <Link
                    to="#"
                    className="btn btn-danger"
                    data-bs-dismiss="modal"
                    onClick={handleDelete}
                  >
                    Yes, Delete
                  </Link>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default FeeConfigModal;
